/**
 * Collection sort and filter drawers, mobile only.
 *
 * Two drawers, one per toolbar button. Opening the filter drawer is left to the
 * theme's own js-collection-side-toggle handler, which puts .open on #sidebar.
 * Opening the sort drawer is ours. Everything else is here:
 *
 *   - closing, by backdrop, by a drawer's close button, or by Escape
 *   - showing and fading the backdrop alongside whichever drawer is out
 *   - keeping the two from being open at once
 *   - holding the page still behind a drawer, so a scroll gesture inside it
 *     cannot run on and scroll the products underneath
 *   - making the sort dropdown dismissable without picking anything
 *
 * State is read from the drawers themselves rather than tracked here, since one
 * of the open actions belongs to the theme. Observers keep everything in step
 * however the class was set, including across the section being refetched.
 */
(function () {
  "use strict";

  var selectors = {
    drawer: '.collection__page-sidebar, .collection__page-sort-drawer',
    sortDrawer: '.collection__page-sort-drawer',
    sortToggle: '.js-collection-sort-toggle',
    backdrop: '.collection__sidebar-backdrop',
    close: '.js-collection-side-close',
    sortWrapper: '.js-hz-filter',
    sortList: '.js-hz-filter-list',
    sortInput: '.js-hz-filter-input'
  };

  var classes = {
    open: 'open',
    visible: 'is-visible',
    // Lets the stylesheet lift #MainContent, which is a stacking context and
    // otherwise keeps a drawer trapped below the site header. See the note by
    // the rule in assets/stylesheet.css.
    pageOpen: 'collection-drawer-open'
  };

  // Matches the drawer transition in the stylesheet.
  var TRANSITION_MS = 300;

  var observed = [];
  var classObservers = [];
  var scrollY = 0;
  var releaseTimer = null;
  var sortWasOpen = false;
  var lastOpen = null;

  /**
   * @description The drawers as they exist right now.
   *
   * Looked up each time rather than held, because applying a filter makes the
   * theme refetch the section and swap the markup out. A reference taken at
   * startup points at a detached element from then on, and reading the open
   * state off it would report whatever was true before the swap.
   *
   * @return {Array} Drawer elements.
   */
  function _drawers() {
    return Array.prototype.slice.call(document.querySelectorAll(selectors.drawer));
  }

  /**
   * @description Drawers currently out.
   * @return {Array} Open drawer elements.
   */
  function _openDrawers() {
    return _drawers().filter(function (el) {
      return el.classList.contains(classes.open);
    });
  }

  /**
   * @description Whether any drawer is out.
   * @return {Boolean} Open state.
   */
  function _isOpen() {
    return _openDrawers().length > 0;
  }

  /**
   * @description Close every drawer. The theme's toggle reads the same class, so
   * removing it here leaves its trigger able to open it again.
   */
  function _close() {
    _drawers().forEach(function (el) {
      el.classList.remove(classes.open);
    });
  }

  /**
   * @description Open one drawer, closing the other.
   * @param  {HTMLElement} drawer The drawer to open.
   */
  function _open(drawer) {
    _drawers().forEach(function (el) {
      if (el !== drawer) {
        el.classList.remove(classes.open);
      }
    });

    drawer.classList.add(classes.open);
  }

  /**
   * @description Pin the page behind the drawer without losing the reading
   * position. Fixing the body is the one approach that also stops iOS Safari
   * scrolling the page under an overlay.
   * @param  {Boolean} locked Whether to hold the page still.
   */
  function _lockPage(locked) {
    var body = document.body;

    if (locked) {
      if (body.style.position === 'fixed') {
        return;
      }

      scrollY = window.pageYOffset;
      body.style.position = 'fixed';
      body.style.top = -scrollY + 'px';
      body.style.left = '0';
      body.style.right = '0';
      return;
    }

    if (!body.style.position) {
      return;
    }

    body.style.removeProperty('position');
    body.style.removeProperty('top');
    body.style.removeProperty('left');
    body.style.removeProperty('right');
    window.scrollTo(0, scrollY);
  }

  /**
   * @description Bring the backdrop in and out with the drawers. It is taken out
   * of the document between times so it cannot swallow taps while invisible.
   * @param  {Boolean} open Whether any drawer is out.
   */
  function _syncBackdrop(open) {
    var backdrop = document.querySelector(selectors.backdrop);

    if (!backdrop) {
      return;
    }

    if (open) {
      backdrop.removeAttribute('hidden');

      // Flushed so the fade runs from the hidden state rather than skipping it.
      void backdrop.offsetWidth;
      backdrop.classList.add(classes.visible);
      return;
    }

    backdrop.classList.remove(classes.visible);
    window.setTimeout(function () {
      if (!_isOpen()) {
        backdrop.setAttribute('hidden', '');
      }
    }, TRANSITION_MS);
  }

  /**
   * @description Shut any open sort dropdown that the pointer landed outside of.
   *
   * The theme closes these on an outside click of its own, but horizontalFilters
   * is declared twice in theme.js and only the later one is wired up, so which
   * listeners exist is not something to depend on. Closing here as well is
   * harmless when the theme already did it. The input is blurred too, since
   * leaving it focused lets the theme's own focus handler reopen the list.
   *
   * @param  {EventTarget} target Whatever was clicked, or null to close all.
   */
  function _closeSortDropdowns(target) {
    document.querySelectorAll(selectors.sortList).forEach(function (list) {
      if (!list.classList.contains(classes.open)) {
        return;
      }

      var wrapper = list.closest(selectors.sortWrapper);

      if (wrapper && target && wrapper.contains(target)) {
        return;
      }

      list.classList.remove(classes.open);

      var input = wrapper ? wrapper.querySelector(selectors.sortInput) : null;

      if (input) {
        input.blur();
      }
    });
  }

  /**
   * @description The dropdown belonging to a sort input.
   * @param  {HTMLElement} input The sort input.
   * @return {HTMLElement} The list, or null.
   */
  function _listFor(input) {
    var wrapper = input.closest(selectors.sortWrapper);

    return wrapper ? wrapper.querySelector(selectors.sortList) : null;
  }

  /**
   * @description Make the sort input open and close its own dropdown.
   *
   * The theme only opens, on focus, so a second click on an input that already
   * has focus fires no focus event and nothing happens. The list stays open with
   * no obvious way to dismiss it.
   *
   * The decision cannot be made from the state at click time, because on a first
   * click focus has already fired and opened the list by then, and the click
   * would immediately close it again. So the state is read on pointerdown, which
   * runs before focus, and the click acts on what it found.
   *
   * @param  {HTMLElement} input The sort input that was clicked.
   */
  function _toggleSortDropdown(input) {
    var list = _listFor(input);

    if (!list) {
      return;
    }

    if (sortWasOpen) {
      list.classList.remove(classes.open);
      input.blur();
      return;
    }

    list.classList.add(classes.open);
  }

  /**
   * @description React to a drawer opening or closing, whoever caused it.
   */
  function _handleStateChange() {
    var openList = _openDrawers();

    // Both out at once means the theme's trigger opened one while ours was
    // already there. The newcomer is whichever is not the one last seen.
    if (openList.length > 1) {
      var newest = openList.filter(function (el) { return el !== lastOpen; })[0] || openList[0];

      openList.forEach(function (el) {
        if (el !== newest) {
          el.classList.remove(classes.open);
        }
      });

      openList = [newest];
    }

    lastOpen = openList[0] || null;

    var open = openList.length > 0;

    window.clearTimeout(releaseTimer);

    if (!open) {

      // A dropdown left open would still be showing the next time a drawer is
      // pulled out.
      _closeSortDropdowns(null);
    }

    if (open) {
      document.body.classList.add(classes.pageOpen);
    } else {

      // Held until the drawer has finished sliding away. Dropping it the moment
      // the drawer starts to close puts the header back over a drawer that is
      // still on screen.
      releaseTimer = window.setTimeout(function () {
        if (!_isOpen()) {
          document.body.classList.remove(classes.pageOpen);
        }
      }, TRANSITION_MS);
    }

    _syncBackdrop(open);
    _lockPage(open);
  }

  /**
   * @description Point the class observers at whichever drawers are in the
   * document now, and bring everything back in step with them.
   *
   * Applying a filter makes the theme refetch the section and replace the
   * markup. The observers were watching elements that got thrown away, so
   * nothing noticed that the replacements came back closed, and the page stayed
   * pinned behind a drawer that was no longer there.
   */
  function _trackDrawers() {
    var current = _drawers();
    var same = current.length === observed.length && current.every(function (el, i) {
      return el === observed[i];
    });

    if (same) {
      return;
    }

    observed = current;
    lastOpen = null;

    classObservers.forEach(function (o) { o.disconnect(); });
    classObservers = current.map(function (el) {
      var observer = new MutationObserver(_handleStateChange);

      observer.observe(el, { attributes: true, attributeFilter: ['class'] });

      return observer;
    });

    // The replacements are closed, so this is what releases the page.
    _handleStateChange();
  }

  /**
   * @description Initialise.
   */
  function init() {
    if (!_drawers().length) {
      return;
    }

    // Runs before focus, so it sees the state the visitor meant to act on.
    document.addEventListener('pointerdown', function (event) {
      if (!event.target || !event.target.closest) {
        return;
      }

      var input = event.target.closest(selectors.sortInput);
      var list = input ? _listFor(input) : null;

      sortWasOpen = !!(list && list.classList.contains(classes.open));
    });

    document.addEventListener('click', function (event) {
      if (!event.target || !event.target.closest) {
        return;
      }

      var sortInput = event.target.closest(selectors.sortInput);

      if (sortInput) {
        _toggleSortDropdown(sortInput);
        return;
      }

      if (event.target.closest(selectors.sortToggle)) {
        event.preventDefault();

        var drawer = document.querySelector(selectors.sortDrawer);

        if (drawer) {
          _open(drawer);
        }

        return;
      }

      _closeSortDropdowns(event.target);

      if (event.target.closest(selectors.close)) {
        event.preventDefault();
        _close();
      }
    });

    document.addEventListener('keydown', function (event) {
      if (event.key !== 'Escape') {
        return;
      }

      // An open dropdown is the innermost thing, so it goes first and the drawer
      // survives. A second Escape then closes the drawer.
      if (document.querySelector(selectors.sortList + '.' + classes.open)) {
        _closeSortDropdowns(null);
        return;
      }

      _close();
    });

    _trackDrawers();

    // Cheap on purpose: one lookup per mutation batch, and it only does work
    // when the drawers have actually been swapped.
    new MutationObserver(_trackDrawers).observe(document.body, {
      childList: true,
      subtree: true
    });

    // A page restored from the back/forward cache can come back with the pinned
    // body still applied.
    window.addEventListener('pageshow', function () {
      if (!_isOpen()) {
        _lockPage(false);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  window.WAU = window.WAU || {};
  window.WAU.CollectionDrawer = { close: _close };
})();
